import * as React from 'react'
import styled from 'styled-components'
import { Range, getTrackBackground } from 'react-range'
import { margin, MarginProps } from 'styled-system'

import { colors } from 'utils/colors'

const STEP = 1

type Props = {
  rtl?: boolean
  min?: number
  max?: number
  value: number[]
  handleChange?: (value: number[]) => void
  disabled: boolean
} & MarginProps

const MultiThumbRangeInput: React.FC<Props> = ({
  rtl = false,
  min = 0,
  max = 100,
  handleChange,
  value,
  disabled = false,
  ...props
}) => {
  const [values, setValues] = React.useState(value)

  React.useEffect(() => {
    setValues(value)
  }, [value])

  const onChange = (values: number[]) => {
    setValues(values)
    if (handleChange) handleChange(values)
  }

  const { minVal, maxVal } = React.useMemo(() => ({ minVal: min, maxVal: max }), [min, max])

  const rangeColor = React.useMemo(() => [colors.gray2, ...Array(values.length - 1).fill(colors.blue2), colors.gray2], [
    values.length,
  ])

  return (
    <RangeContainer {...props}>
      <Range
        disabled={disabled}
        values={values}
        step={STEP}
        min={minVal}
        max={maxVal}
        rtl={rtl}
        allowOverlap={false}
        onChange={onChange}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{
              ...props.style,
              height: '15px',
              display: 'flex',
              width: '100%',
            }}
          >
            <div
              ref={props.ref}
              style={{
                height: '8px',
                width: '100%',
                borderRadius: '6px',
                background: getTrackBackground({
                  values,
                  colors: rangeColor,
                  min: minVal,
                  max: maxVal,
                  rtl,
                }),
                alignSelf: 'center',
              }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={({ props, isDragged }) => (
          <div
            {...props}
            key={props.key}
            style={{
              ...props.style,
              height: '12px',
              width: '12px',
              borderRadius: '50%',
              backgroundColor: isDragged ? colors.blue3 : colors.blue1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: 'rgba(0, 0, 0, 0.4) 0px 2px 6px',
            }}
          ></div>
        )}
      />
    </RangeContainer>
  )
}

const RangeContainer = styled.div<Partial<MarginProps>>`
  ${margin}

  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`

export default React.memo(MultiThumbRangeInput)
